/**
 * Shared setup of the time-window RangePickers (list page filters, dashboard
 * window): quick look-backs, picker props, and the conversion between picker
 * values (dayjs, browser-local) and the URL/API bounds (ISO-8601 UTC).
 */
import dayjs from "dayjs";
import { toQueryInstant } from "./logQuery";

/**
 * Quick look-backs. Computed when picked, not when the module loads, so a
 * tab left open overnight still gets a fresh "now". `t` has i18next's
 * `t(key, fallback)` shape; without it the English labels are used.
 */
export const buildWindowPresets = (t = (_key, fallback) => fallback) => [
  {
    label: t("mcapLogs.window.last24Hours", "Last 24 hours"),
    value: () => [dayjs().subtract(24, "hour"), dayjs()],
  },
  {
    label: t("mcapLogs.window.last7Days", "Last 7 days"),
    value: () => [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: t("mcapLogs.window.last30Days", "Last 30 days"),
    value: () => [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: t("mcapLogs.window.last90Days", "Last 90 days"),
    value: () => [dayjs().subtract(89, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: t("mcapLogs.window.thisMonth", "This month"),
    value: () => [dayjs().startOf("month"), dayjs().endOf("month")],
  },
  {
    label: t("mcapLogs.window.lastMonth", "Last month"),
    value: () => [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
];

/**
 * Minute precision with the day's bounds pre-filled, so a date-only pick
 * means whole days; either end may be left open ("everything since…" /
 * "…up to").
 */
export const WINDOW_PICKER_PROPS = {
  format: "YYYY-MM-DD HH:mm",
  showTime: {
    format: "HH:mm",
    defaultValue: [dayjs().startOf("day"), dayjs().endOf("day")],
  },
  allowEmpty: [true, true],
};

/**
 * Picker dates → URL/API bounds. The picker works in the browser's local
 * time while the URL and API carry UTC instants; the bounds are widened to
 * whole minutes first so "to 18:00" includes the 18:00 minute.
 */
export const windowToBounds = (dates) => {
  const [from, to] = dates || [];
  return {
    from: from ? toQueryInstant(from.startOf("minute")) : undefined,
    to: to ? toQueryInstant(to.endOf("minute")) : undefined,
  };
};

/** URL bounds → picker value (null when the window is unset). */
export const boundsToWindow = (from, to) =>
  from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null;
